import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, unref as _unref, resolveComponent as _resolveComponent, isRef as _isRef } from "vue"

const _hoisted_1 = { class: "mb-l pb-xs border-gray-200 border-b-2 flex items-center justify-between" }
const _hoisted_2 = { class: "flex justify-between items-center" }
const _hoisted_3 = { class: "flex items-center" }
const _hoisted_4 = /*#__PURE__*/_createElementVNode("small", null, "Nur der Administrator kann die Zugriffe bearbeiten/löschen.", -1)
const _hoisted_5 = ["value"]

import { computed, ref, onMounted } from "vue";
import { useManagedPatientsStore } from "@/store/managedPatientsStore";
import router from "@/router";

import { ManagedPatient, ManagedPatientKey } from "@/interfaces/managedPatient";
import { supabase } from "@/helpers/supabase";
import { useSessionStore } from "@/store/sessionStore";
import { usePublicStore } from "@/store/publicStore";
import { bytesToHex, encryptAsClient } from "@/helpers/encryption";
import { CareProviderGroup } from "@/interfaces/careProviderGroup";
import DialogCard from "@/components/DialogCard.vue";
import UmanaHeadline from "@/components/UmanaHeadline.vue";
import IconButton from "@/components/IconButton.vue";
import ListTile from "@/components/ListTile.vue";
import { useNotificationStore } from "@/store/notificationStore";
import ToolTip from "@/components/ToolTip.vue";
import CoreIcon from "@/components/CoreIcon.vue";
import { encryptSymmetric } from "@/helpers/encryption";


export default /*#__PURE__*/_defineComponent({
  __name: 'PatientAddCareProviderGroup',
  props: {
    managedPatient: {},
    managedPatientKey: {}
  },
  setup(__props: any) {

const managedPatientsStore = useManagedPatientsStore();

const sessionStore = useSessionStore();

const isInviting = ref<boolean>(false);
const props = __props;
const newCareProviderGroup = ref<CareProviderGroup | null>();
const managedPatient = computed(() => props.managedPatient);

const connectedCareProviderGroups = computed(() => {
  if (
    managedPatientsStore.connectedCareProviderGroups &&
    managedPatient.value
  ) {
    let set = managedPatientsStore.connectedCareProviderGroups.get(
      managedPatient.value.id
    );
    let careProviderGroups = new Array<CareProviderGroup>();
    set?.forEach((cpgId) => {
      let cpg = publicStore.careProviderGroups?.find((cpg) => cpg.id == cpgId);

      if (cpg) {
        careProviderGroups.push(cpg);
      }
    });
    return careProviderGroups.sort((cpg, cpg2) => cpg.id - cpg2.id);
  } else {
    console.error("Patient not available");
    return new Set<CareProviderGroup>();
  }
});

onMounted(() => {
  if (managedPatient.value == undefined) {
    router.push("/managedPatients");
  }
});

// share
const publicStore = usePublicStore();

const selectedCareProviderGroup = computed(() => {
  return sessionStore.selectedCareProviderGroup;
});

const careProviderGroups = computed(() => {
  return usePublicStore().oppositeGroups;
});

let selectedCpg: string | undefined;
async function share() {
  if (!selectedCpg || !selectedCareProviderGroup.value?.id) return;

  const patientKeys = Array.from(
    managedPatientsStore.keys?.values() ?? []
  ).filter((mp) => mp.managed_patient_id == managedPatient.value?.id);
  const myPublicKey = sessionStore.getCurrentPublicKey();
  const receiverPublicKey = publicStore.latestPublicKeyOfGroup(
    parseInt(selectedCpg)
  );
  const privateKey = sessionStore.getCurrentPrivateKey();

  if (
    selectedCareProviderGroup.value?.id &&
    myPublicKey &&
    receiverPublicKey &&
    privateKey
  ) {
    let encryptedKeys = [];

    for (let patientKey of patientKeys) {
      if (!patientKey.decrypted_symmetric_key) {
        useNotificationStore().notifications.push({
          msg: `Fehler - Patientenschlüssel nicht vorhanden. #ep01`,
          type: "danger",
        });
        return;
      }
      const symmetricKey = patientKey.decrypted_symmetric_key;

      let key = {
        key: await encryptAsClient(
          myPublicKey,
          privateKey,
          receiverPublicKey,
          symmetricKey
        ),
        keyId: patientKey.key_id,
      };
      encryptedKeys.push(key);
      const result = await supabase
        .from("careProviderGroup_managedPatient")
        .insert([
          {
            care_provider_group_id: selectedCpg,
            managed_patient_id: managedPatient.value?.id,
            key_id: patientKey.key_id,
            nonce: bytesToHex(key.key.nonce),
            encrypted_symmetric_key: bytesToHex(key.key.encryptedMsg),
            used_public_key_id: receiverPublicKey.keyId,
            used_private_key_id: privateKey.keyId,
          },
        ]);

      if (result.error) {
        useNotificationStore().notifications.push({
          msg: `Fehler - Patientenschlüssel nicht vorhanden. #ep02: ${result.error}`,
          type: "danger",
        });
        return;
      }
      window._paq.push([
        "trackEvent",
        "PatientAddCareProviderGroup",
        "added",
        undefined,
      ]);
      isInviting.value = false;
    }
  } else {
    useNotificationStore().notifications.push({
      msg: `Fehler - Gruppe oder Schlüssel nicht vorhanden. #ep03: ${selectedCareProviderGroup.value?.id}, ${myPublicKey}, ${receiverPublicKey}, ${selectedCpg}`,
      type: "danger",
    });
  }
}

async function deleteShare(careProviderGroupId: number) {
  if (!managedPatient.value?.id) {
    console.error("NO PATIENT SELECTED");
    return;
  }
  try {
    const result = await supabase
      .from("careProviderGroup_managedPatient")
      .delete()
      .eq("care_provider_group_id", careProviderGroupId)
      .eq("managed_patient_id", managedPatient.value.id);

    if (result.error != null) {
      console.error("Error removing CP", result.error);
      return false;
    }

    await removeMedicationByCPGId(careProviderGroupId);

    await newKey();

    return true;
  } catch (e) {
    console.error("WHAT?", e);
    return false;
  }
}

// TODO: At the moment this method is basically the same as in PatientMedications.vue so somehow one have to outsource the logic of this medthod.
async function removeMedicationByCPGId(cpgId: number) {
  let oldMedication = JSON.parse(
    JSON.stringify(managedPatient.value.currentMedication)
  );
  let newMedication = oldMedication.filter((cm) => cm.supplierId !== cpgId);
  await encryptAndSaveMedication(newMedication);
  window._paq.push(["trackEvent", "PatientMedication", "delete", undefined]);
}

// TODO: At the moment this method is basically the same as in PatientMedications.vue so somehow one have to outsource the logic of this medthod.
async function encryptAndSaveMedication(newMedication: Array<any>) {
  if (
    !props.managedPatientKey.decrypted_symmetric_key ||
    !managedPatient.value?.id ||
    !sessionStore.selectedCareProviderGroup?.id
  )
    return;

  const encryptedMedication = await encryptSymmetric(
    JSON.stringify(newMedication),
    props.managedPatientKey.decrypted_symmetric_key
  );

  await supabase
    .from("managedPatient_data")
    .insert({
      managed_patient_id: managedPatient.value.id,
      used_key_id: props.managedPatientKey.key_id,
      nonce: encryptedMedication.nonce,
      encrypted_data: encryptedMedication.encryptedMessage,
      owner_care_provider_group_id: sessionStore.selectedCareProviderGroup.id,
      type: 2,
    })
    .returns<ManagedPatient[]>();

  await managedPatientsStore.updatePatients();
}

async function newKey() {
  await managedPatientsStore.replaceManagedPatientKey(props.managedPatient.id);
}

async function setOwner(careProviderGroupId: number | undefined) {
  if (careProviderGroupId === undefined) return;
  newCareProviderGroup.value = null;

  await managedPatientsStore.setOwner(
    careProviderGroupId,
    managedPatient.value.id
  );
}

return (_ctx: any,_cache: any) => {
  const _component_FormKit = _resolveComponent("FormKit")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(UmanaHeadline, { h: "h2" }, {
        default: _withCtx(() => [
          _createTextVNode(" Zugriffe ")
        ]),
        _: 1
      }),
      (
          managedPatient.value?.owner_care_provider_group_id ==
          selectedCareProviderGroup.value?.id
        )
        ? (_openBlock(), _createBlock(IconButton, {
            key: 0,
            onClick: _cache[0] || (_cache[0] = ($event: any) => (isInviting.value = true)),
            icon: "plus"
          }))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("ul", null, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(connectedCareProviderGroups.value, (connectedCareProviderGroup) => {
        return (_openBlock(), _createBlock(ListTile, {
          key: connectedCareProviderGroup.id
        }, {
          main: _withCtx(() => [
            _createElementVNode("div", _hoisted_2, [
              _createTextVNode(_toDisplayString(connectedCareProviderGroup.group_name) + " ", 1),
              _createElementVNode("div", _hoisted_3, [
                _createVNode(ToolTip, {
                  position: "left",
                  content: "Als Administrator festlegen"
                }, {
                  default: _withCtx(() => [
                    (
                    managedPatient.value?.owner_care_provider_group_id ==
                      selectedCareProviderGroup.value?.id &&
                    connectedCareProviderGroup.id !=
                      selectedCareProviderGroup.value?.id
                  )
                      ? (_openBlock(), _createBlock(CoreIcon, {
                          key: 0,
                          onClick: ($event: any) => (newCareProviderGroup.value = connectedCareProviderGroup),
                          icon: "user-change"
                        }, null, 8, ["onClick"]))
                      : _createCommentVNode("", true)
                  ]),
                  _: 2
                }, 1024),
                (
                  managedPatient.value?.owner_care_provider_group_id ==
                    selectedCareProviderGroup.value?.id &&
                  connectedCareProviderGroup.id != selectedCareProviderGroup.value?.id
                )
                  ? (_openBlock(), _createBlock(CoreIcon, {
                      key: 0,
                      onClick: ($event: any) => (deleteShare(connectedCareProviderGroup.id)),
                      icon: "trash",
                      class: "ml-xs"
                    }, null, 8, ["onClick"]))
                  : _createCommentVNode("", true),
                (
                  managedPatient.value?.owner_care_provider_group_id ==
                  connectedCareProviderGroup?.id
                )
                  ? (_openBlock(), _createBlock(ToolTip, {
                      key: 1,
                      position: "left",
                      content: "Administrator"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(CoreIcon, { icon: "user" })
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true)
              ])
            ])
          ]),
          _: 2
        }, 1024))
      }), 128))
    ]),
    _hoisted_4,
    _createVNode(DialogCard, {
      "is-visible": isInviting.value,
      onClose: _cache[2] || (_cache[2] = ($event: any) => (isInviting.value = false)),
      title: "Unternehmen hinzufügen",
      "show-footer": false
    }, {
      default: _withCtx(() => [
        _createVNode(_component_FormKit, {
          type: "form",
          onSubmit: share,
          "submit-label": "Zugriff auf Patient teilen"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_FormKit, {
              type: "select",
              modelValue: _unref(selectedCpg),
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (_isRef(selectedCpg) ? (selectedCpg).value = $event : selectedCpg = $event))
            }, {
              default: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(careProviderGroups.value, (cpg) => {
                  return (_openBlock(), _createElementBlock("option", {
                    key: cpg.id,
                    value: cpg.id
                  }, _toDisplayString(cpg.group_name), 9, _hoisted_5))
                }), 128))
              ]),
              _: 1
            }, 8, ["modelValue"])
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["is-visible"]),
    _createVNode(DialogCard, {
      "is-visible": newCareProviderGroup.value != null,
      onClose: _cache[3] || (_cache[3] = ($event: any) => (newCareProviderGroup.value = null)),
      title: "Administrator ändern",
      "show-footer": true,
      onSubmit: _cache[4] || (_cache[4] = ($event: any) => (setOwner(newCareProviderGroup.value?.id)))
    }, {
      default: _withCtx(() => [
        _createTextVNode(" Möchten Sie den Administrator zu \"" + _toDisplayString(newCareProviderGroup.value?.group_name) + "\" ändern? Sie werden als Administrator entfernt, können aber weiterhin den Patienten ansehen und verändern. Sie können jedoch die Zugriffe nicht mehr verändern oder den Administrator ändern. ", 1)
      ]),
      _: 1
    }, 8, ["is-visible"])
  ]))
}
}

})